import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Search,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { AvailableAfesResponse } from 'types/api-responses';
import styles from './edit-afe-source-modal.module.scss';
import { AfeList } from './afe-list';

export interface EditAfeSourceModalViewProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when save is clicked with the selected AFE and its costs */
  onSave: (
    afeId: string,
    afeHref?: string,
    costs?: {
      drillingCost?: number;
      completionCost?: number;
      facilitiesCost?: number;
      contingenciesCost?: number;
      otherCost?: number;
    },
    afeWellName?: string
  ) => void;
  /** The slot ID being edited. Ignored for display when `label` is supplied. */
  slotId: string;
  /**
   * Overrides the default "Slot Id: …" header label. For callers that are not
   * editing a slot — the Expense Scenario area-level fallback AFE names its
   * area instead. Unset keeps the existing slot phrasing.
   */
  label?: string;
  /**
   * Notified when the search box changes, for callers whose candidate list is
   * larger than one page and must be narrowed server-side. The in-memory filter
   * below still runs, so a caller that ignores this keeps today's behaviour.
   */
  onSearchChange?: (query: string) => void;
  /** Optional well name for display */
  wellName?: string;
  /** The current AFE source ID (for determining if selection has changed) */
  currentAfeSourceId?: string;
  /** Candidate AFEs; `null` until loaded. Supplied by the data wrapper. */
  data: AvailableAfesResponse | null;
  /** Whether the candidate AFEs are loading. */
  isLoading: boolean;
  /** Load error message, or `null`. */
  error: string | null;
}

/**
 * Presentational Edit AFE Source modal: search + selectable AFE list (with cost
 * columns), with the candidate data supplied as props. Data fetching is owned by
 * the caller (a Jotai wrapper on the legacy page, a React Query hook in
 * eval3-new), so this core stays decoupled from any data source.
 *
 * @param {EditAfeSourceModalViewProps} props - Modal state, callbacks, and candidate data.
 * @return {JSX.Element} The modal element.
 */
export const EditAfeSourceModalView = ({
  isOpen,
  onClose,
  onSave,
  slotId,
  label,
  onSearchChange,
  wellName,
  currentAfeSourceId,
  data,
  isLoading,
  error,
}: EditAfeSourceModalViewProps) => {
  const [selectedAfeId, setSelectedAfeId] = useState<string | null>(
    currentAfeSourceId ?? null
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Reset selection + search when the modal opens for a (new) slot.
  useEffect(() => {
    if (isOpen && slotId) {
      setSelectedAfeId(currentAfeSourceId ?? null);
      setSearchQuery('');
    }
  }, [isOpen, slotId, currentAfeSourceId]);

  // Filter AFEs based on search query
  const filteredAfes = useMemo(() => {
    if (!data) return [];

    const afes = data.availableAfes;
    if (!searchQuery) return afes;

    const lowerQuery = searchQuery.toLowerCase();
    return afes.filter(
      (afe) =>
        String(afe.id).toLowerCase().includes(lowerQuery) ||
        afe.well?.id?.toLowerCase().includes(lowerQuery) ||
        afe.well?.name?.toLowerCase().includes(lowerQuery) ||
        afe.afeType?.toLowerCase().includes(lowerQuery)
    );
  }, [data, searchQuery]);

  const hasSelectionChanged = selectedAfeId !== currentAfeSourceId;

  const handleSave = () => {
    if (selectedAfeId) {
      const selectedAfe = data?.availableAfes.find(
        (a) => a.id === selectedAfeId
      );
      const href = selectedAfe?.href ?? undefined;
      const costs = selectedAfe
        ? {
            drillingCost: selectedAfe.drillingCost,
            completionCost: selectedAfe.completionCost,
            facilitiesCost: selectedAfe.facilitiesCost,
            contingenciesCost: selectedAfe.contingenciesCost,
            otherCost: selectedAfe.otherCost,
          }
        : undefined;
      onSave(selectedAfeId, href, costs, selectedAfe?.well?.name);
    }
  };

  // Callers that are not editing a slot (the Expense Scenario area-level
  // fallback AFE) supply their own label. Left unset the slot phrasing is
  // unchanged, so the two existing callers render exactly as before.
  const modalLabel =
    label ?? `Slot Id: ${slotId}${wellName ? ` | Well Name: ${wellName}` : ''}`;

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="Edit AFE Source"
      modalLabel={modalLabel}
      primaryButtonText="Save"
      primaryButtonDisabled={!hasSelectionChanged || !selectedAfeId}
      onRequestSubmit={handleSave}
      secondaryButtonText="Cancel"
      size="lg"
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        {error && (
          <InlineNotification
            kind="error"
            title={error}
            lowContrast
            hideCloseButton
          />
        )}

        <p className={styles.helperText}>Select an AFE source below</p>

        <div className={styles.searchListContainer}>
          <Search
            placeholder="Search input text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              onSearchChange?.(e.target.value);
            }}
            labelText="Search"
            closeButtonLabelText="Clear search"
            size="lg"
            className={styles.searchInput}
          />

          {isLoading ? (
            <DataTableSkeleton
              showHeader={false}
              showToolbar={false}
              columnCount={3}
              rowCount={5}
            />
          ) : (
            <AfeList
              afes={filteredAfes}
              selectedId={selectedAfeId}
              onSelect={setSelectedAfeId}
            />
          )}

          <p className={styles.rowCount}>
            {filteredAfes.length} {filteredAfes.length === 1 ? 'Row' : 'Rows'}
          </p>
        </div>
      </div>
    </Modal>
  );
};
